import { Component, OnInit } from '@angular/core'
import { Meta, Title } from '@angular/platform-browser'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { Apollo, gql } from 'apollo-angular'
import { Subscription } from 'rxjs'
import { filter } from 'rxjs/operators'

interface Image {
  type: string
  url: string
  title: string
  alt: string
  blur: string
}

interface Text {
  type: string
  text: string
}

interface ImageText {
  type: string
  text: string
  url: string
  title: string
  alt: string
  blur: string
  standing: boolean
}

interface Gallery {
  type: string
  images: Image[]
}

type ContentArray = (Image | Text | ImageText | Gallery)[]

enum ContentType {
  IMAGE = 'IMAGE',
  TEXT = 'TEXT',
  IMAGETEXT = 'IMAGETEXT',
  GALLERY = 'GALLERY',
}

@Component({
  selector: 'app-page',
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.css'],
})
export class PageComponent implements OnInit {
  title?: string
  body?: string
  content?: ContentArray
  ContentType = ContentType

  private dataSub?: Subscription
  private paramSub?: Subscription
  private routerSub?: Subscription

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apollo: Apollo,
    private titleService: Title,
    private metaService: Meta,
  ) {}

  ngOnInit(): void {
    const lang = this.route.snapshot.paramMap.get('lang') || 'sv'
    const slug = this.route.snapshot.url[1]?.path || 'home'

    this.data(lang, slug)

    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const lang = this.route.snapshot.paramMap.get('lang') || 'sv'
        const slug = this.route.snapshot.url[1]?.path || 'home'

        this.data(lang, slug)
      })
  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe()
    this.paramSub?.unsubscribe()
    this.routerSub?.unsubscribe()
  }

  private data(lang: string, slug: string): void {
    this.dataSub?.unsubscribe()

    this.dataSub = this.apollo
      .watchQuery({
        query: gql`
          {
            page(locale: ${lang}, filter: {slug: {eq: "${slug}"}}) {
              id
              seo {
                description
                title
                image {
                  url(imgixParams: {w: "600"})
                }
              }
              title(locale: ${lang})
              headline(locale: ${lang})
              body(locale: ${lang}, markdown: true)
              content(locale: ${lang}) {
                ... on TextRecord {
                  id
                  text(markdown: true)
                }
                ... on ImageRecord {
                  id
                  image {
                    alt(locale: ${lang})
                    url(imgixParams: {w: "800"})
                    id
                    title(locale: ${lang})
                    blurUpThumb
                  }
                }
                ... on ImageTextRecord {
                  id
                  text(markdown: true)
                  image {
                    alt(locale: ${lang})
                    url(imgixParams: {w: "500"})
                    id
                    title(locale: ${lang})
                    blurUpThumb
                    responsiveImage {
                      aspectRatio
                    }
                  }
                }
                ... on GalleryRecord {
                  id
                  images {
                    alt(locale: ${lang})
                    url(imgixParams: {w: "500"})
                    id
                    title(locale: ${lang})
                    blurUpThumb
                  }
                }
              }
            }
          }
        `,
      })
      .valueChanges.subscribe((result: any) => {
        const page = result?.data?.page

        if (!page) return

        this.title = page.headline
        this.body = page.body

        this.titleService.setTitle(page.seo?.title)
        this.metaService.updateTag({
          name: 'description',
          content: page.seo?.description,
        })
        this.metaService.updateTag({
          property: 'og:image',
          content: page.seo?.image?.url,
        })

        this.content = page.content
          .map((i) => {
            if (i.__typename === 'ImageRecord' && i.image) {
              const image: Image = {
                type: ContentType.IMAGE,
                url: i.image.url || '',
                title: i.image.title || '',
                alt: i.image.alt || '',
                blur: i.image.blurUpThumb || '',
              }
              return image
            }

            if (i.__typename === 'TextRecord') {
              const text: Text = {
                type: ContentType.TEXT,
                text: i.text || '',
              }

              return text
            }

            if (i.__typename === 'ImageTextRecord') {
              const imageText: ImageText = {
                type: ContentType.IMAGETEXT,
                text: i.text || '',
                url: i.image.url || '',
                title: i.image.title || '',
                alt: i.image.alt || '',
                blur: i.image.blurUpThumb || '',
                standing: i.image.responsiveImage.aspectRatio < 1,
              }

              return imageText
            }

            if (i.__typename === 'GalleryRecord') {
              const gallery: Gallery = {
                type: ContentType.GALLERY,
                images: i.images.map((img) => {
                  const image: Image = {
                    type: ContentType.IMAGE,
                    url: img.url || '',
                    title: img.title || '',
                    alt: img.alt || '',
                    blur: img.blurUpThumb || '',
                  }
                  return image
                }),
              }

              return gallery
            }
            return undefined
          })
          .filter((i) => !!i)
      })
  }
}
